import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Alert, Box, Breadcrumbs, Button, Checkbox, Chip, PanelCard, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../lib/CapabilitiesContext';
import { saveBrowserDownload, formatCents, formatShortDate, formatTime } from '../lib/download';
import { vendorApiClient, type VendorOrderDetail, type VendorOrderItem } from '../lib/vendorApiClient';

const MUTED = '#5B6573';
const BORDER = '#DDE4E0';
const ORANGE = '#A85200';

export default function OrderDetail() {
  const { t } = useTranslation();
  const { orderLocationId = '' } = useParams();
  const { activeVendorId, has } = useVendor();
  const [order, setOrder] = useState<VendorOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bagCount, setBagCount] = useState('1');
  const [barcode, setBarcode] = useState('');
  const [sealEvidenceId, setSealEvidenceId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const canFulfill = has('order:fulfill');

  const refresh = useCallback(async () => {
    if (!activeVendorId || !orderLocationId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await vendorApiClient.getOrder(activeVendorId, orderLocationId);
      setOrder(result.order);
      setBagCount(String(result.order.bagCount ?? 1));
      setSealEvidenceId(result.order.evidence.find((item) => item.type === 'vendor_seal_photo')?.evidenceId ?? null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('orderDetail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [activeVendorId, orderLocationId, t]);

  useEffect(() => { void refresh(); }, [refresh]);

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('orderDetail.actionError'));
    } finally {
      setBusy(false);
    }
  };

  const complete = useMemo(() => order?.items.every((item) => item.checked || item.unavailable) ?? false, [order]);
  const downloadPrep = async () => {
    if (!activeVendorId) return;
    const file = await vendorApiClient.downloadOrderPrepPdf(activeVendorId, orderLocationId);
    saveBrowserDownload(file.blob, file.filename);
  };
  const downloadLabels = async () => {
    if (!activeVendorId) return;
    const bags = Number.parseInt(bagCount, 10);
    const file = await vendorApiClient.downloadOrderLabelsPdf(activeVendorId, orderLocationId, Number.isInteger(bags) ? bags : undefined);
    saveBrowserDownload(file.blob, file.filename);
  };
  const uploadSeal = async (file: File) => {
    if (!activeVendorId) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await vendorApiClient.uploadOrderSeal(activeVendorId, orderLocationId, file);
      setSealEvidenceId(saved.evidenceId);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('orderDetail.actionError'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const scanBarcode = async () => {
    if (!activeVendorId || !/^\d{12,13}$/.test(barcode)) return;
    await act(async () => {
      await vendorApiClient.scanOrderItem(activeVendorId, orderLocationId, barcode);
      setBarcode('');
    });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!order) return <Alert severity="error">{error ?? t('orderDetail.notFound')}</Alert>;

  const latestSeal = order.evidence.find((item) => item.evidenceId === sealEvidenceId) ?? order.evidence.find((item) => item.type === 'vendor_seal_photo');
  const canWork = canFulfill && order.isFinal && order.phase !== 'ready' && order.phase !== 'handed_off';

  return (
    <Box data-testid="vendor-order-detail" sx={{ maxWidth: 1040 }}>
      <Breadcrumbs ariaLabel={t('orderDetail.breadcrumb')} linkComponent={Link} items={[{ label: t('orders.title'), to: '/orders' }, { label: order.glpOrderId ?? order.orderLocationId, ariaCurrent: true }]} />
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 800 }}>{t('orderDetail.title', { order: order.glpOrderId ?? order.orderLocationId })}</Typography><Typography sx={{ color: MUTED, mt: 0.5 }}>{t('orderDetail.privacy', { code: order.bagCode })}</Typography></Box>
        <Box sx={{ display: 'flex', gap: 1 }}><Button ariaLabel={t('orderDetail.prepPdf')} buttonText={t('orderDetail.prepPdf')} variant="outlined" startIcon="download" onClick={() => void downloadPrep()} /><Button ariaLabel={t('orderDetail.labelsPdf')} buttonText={t('orderDetail.labelsPdf')} variant="outlined" startIcon="download" onClick={() => void downloadLabels()} /></Box>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {!order.isFinal && <Alert severity="info" title={t('orderDetail.preliminaryTitle')} sx={{ mt: 2 }}>{t('orderDetail.preliminaryBody')}</Alert>}

      <PanelCard as="section" accent="#6D8CA0" sx={{ mt: 2, height: 'auto', bgcolor: '#EDF5FA' }}>
        <Typography sx={{ fontWeight: 800 }}>{order.runner?.firstName ? t('orderDetail.runnerName', { firstName: order.runner.firstName, lastInitial: order.runner.lastInitial ? ` ${order.runner.lastInitial}.` : '' }) : t('orderDetail.runnerPending')}</Typography>
        <Typography variant="body2" sx={{ color: MUTED }}>{runnerLine(order, t('orderDetail.runnerVerified'), t('orderDetail.runnerArrivalPending'))}</Typography>
      </PanelCard>

      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2 }}>
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${BORDER}` }}>
            <Box><Typography sx={{ fontWeight: 800 }}>{t('orderDetail.items', { count: order.items.length })}</Typography><Typography variant="caption" sx={{ color: MUTED }}>{order.location.name} · {formatShortDate(order.runDate)} · {formatTime(order.deliveryWindowStart)}–{formatTime(order.deliveryWindowEnd)}</Typography></Box>
            <Chip label={t(`orders.phase.${order.phase}`)} size="small" />
          </Box>
          {order.items.map((item) => <OrderItemRow key={item.orderItemId} item={item} working={busy || !canWork || order.phase !== 'preparing'} onCheck={(checked) => activeVendorId && act(() => vendorApiClient.checkoffOrderItem(activeVendorId, orderLocationId, item.orderItemId, checked))} onUnavailable={() => activeVendorId && act(() => vendorApiClient.reportOrderItemUnavailable(activeVendorId, orderLocationId, item.orderItemId))} />)}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PanelCard as="section" accent="#F67A1A" title={t('orderDetail.preparation')} sx={{ height: 'auto' }}>
            {order.phase === 'final' && canFulfill && <Button ariaLabel={t('orderDetail.startPrep')} buttonText={t('orderDetail.startPrep')} variant="contained" color="primary" fullWidth onClick={() => activeVendorId && void act(() => vendorApiClient.startOrderPrep(activeVendorId, orderLocationId))} disabled={busy} />}
            {order.phase === 'preparing' && <Typography variant="body2" sx={{ color: MUTED }}>{complete ? t('orderDetail.allResolved') : t('orderDetail.resolveItems')}</Typography>}
            {order.phase === 'preparing' && canFulfill && <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField ariaLabel={t('orderDetail.scanBarcode')} label={t('orderDetail.scanBarcode')} value={barcode} onChange={(event) => setBarcode(event.target.value.replace(/\D/g, '').slice(0, 13))} helperText={t('orderDetail.scanHelp')} disabled={busy} fullWidth />
              <Button ariaLabel={t('orderDetail.checkScan')} buttonText={t('orderDetail.checkScan')} variant="outlinedOrange" onClick={() => void scanBarcode()} disabled={busy || !/^\d{12,13}$/.test(barcode)} />
            </Box>}
            {order.phase === 'ready' && <Typography variant="body2" sx={{ color: '#216E27', fontWeight: 700 }}>{t('orderDetail.readyMessage')}</Typography>}
            {order.phase === 'handed_off' && <Typography variant="body2" sx={{ color: MUTED }}>{t('orderDetail.handedOffMessage')}</Typography>}
          </PanelCard>

          <PanelCard as="section" accent="#25862D" title={t('orderDetail.readyTitle')} sx={{ height: 'auto' }}>
            <Typography variant="body2" sx={{ color: MUTED, mb: 1.5 }}>{t('orderDetail.readyBody')}</Typography>
            {latestSeal?.url && <Box component="img" src={latestSeal.url} alt={t('orderDetail.sealAlt')} sx={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: '8px', mb: 1.5 }} />}
            {order.phase === 'preparing' && canFulfill && <>
              <Box component="input" ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" aria-label={t('orderDetail.addSeal')} onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) void uploadSeal(file); }} disabled={busy} sx={{ width: '100%', mb: 1.5 }} />
              <TextField ariaLabel={t('orderDetail.bagCount')} label={t('orderDetail.bagCount')} value={bagCount} onChange={(event) => setBagCount(event.target.value.replace(/\D/g, '').slice(0, 2))} disabled={busy} fullWidth />
              <Button ariaLabel={t('orderDetail.markReady')} buttonText={t('orderDetail.markReady')} variant="contained" color="success" fullWidth sx={{ mt: 1.5 }} disabled={busy || !complete || !sealEvidenceId || Number(bagCount) < 1} onClick={() => activeVendorId && sealEvidenceId && void act(() => vendorApiClient.markOrderReady(activeVendorId, orderLocationId, Number(bagCount), sealEvidenceId))} />
            </>}
            {order.phase === 'ready' && canFulfill && <Button ariaLabel={t('orderDetail.confirmHandoff')} buttonText={t('orderDetail.confirmHandoff')} variant="contained" color="success" fullWidth onClick={() => activeVendorId && void act(() => vendorApiClient.markOrderHandedOff(activeVendorId, orderLocationId))} disabled={busy} />}
          </PanelCard>
        </Box>
      </Box>
    </Box>
  );
}

function OrderItemRow({ item, working, onCheck, onUnavailable }: { item: VendorOrderItem; working: boolean; onCheck: (checked: boolean) => void; onUnavailable: () => void }) {
  const { t } = useTranslation();
  return <Box sx={{ px: 2.5, py: 1.75, display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1.5, alignItems: 'center', borderBottom: `1px solid ${BORDER}`, bgcolor: item.unavailable ? '#FFF5EC' : '#fff' }}>
    <Checkbox ariaLabel={t('orderDetail.checkItem', { item: item.name })} checked={item.checked} onChange={(event) => onCheck(event.target.checked)} disabled={working || item.unavailable} />
    <Box><Typography sx={{ fontWeight: 700, textDecoration: item.unavailable ? 'line-through' : 'none' }}>{item.quantity} × {item.name}{item.size ? ` · ${item.size}` : ''}</Typography><Typography variant="caption" sx={{ color: MUTED }}>{item.note ?? formatCents(item.unitPriceCents)}{item.unavailable ? ` · ${t('orderDetail.unavailable')}` : ''}</Typography></Box>
    {!item.unavailable && <Button ariaLabel={t('orderDetail.reportUnavailable', { item: item.name })} buttonText={t('orderDetail.unavailable')} variant="text" size="small" onClick={onUnavailable} disabled={working || item.checked} sx={{ color: ORANGE }} />}
  </Box>;
}

function runnerLine(order: VendorOrderDetail, verified: string, pending: string): string {
  const vehicle = order.runner?.vehicle;
  const detail = vehicle ? [vehicle.color, vehicle.make, vehicle.model].filter(Boolean).join(' ') : '';
  return [order.runner?.verificationState ? verified : null, order.runner?.arrivalState, detail].filter(Boolean).join(' · ') || pending;
}
